<template>
  <div class="student-email-report fee-status-report">
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">{{ reportToolbarTitle }}</h4>
        <b-badge v-show="!loading && campuses.length" variant="light-success" class="ml-2">
          {{ totalSectionCount }} Sections
        </b-badge>
        <b-badge
          v-show="!loading && campuses.length > 1"
          variant="light-primary"
          class="ml-1"
        >
          {{ campuses.length }} Campuses
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" type="button" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" type="button" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem" />
      <p class="mt-3 font-weight-bold">{{ loadingMessage }}</p>
    </div>

    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" class="mt-3" @click="loadReportData">
        Retry
      </b-button>
    </div>

    <div v-else-if="!campuses.length" class="report-page text-center py-5">
      <feather-icon icon="InboxIcon" size="32" class="text-muted mb-2" />
      <p class="text-secondary mb-0">No records found for the selected campus</p>
    </div>

    <div v-else class="report-page">
      <div class="report-header-modern mb-4">
        <div class="d-flex align-items-center">
          <div v-if="schoolLogo" class="logo-wrapper mr-3">
            <img :src="schoolLogo" alt="Logo" class="school-logo-img" />
          </div>
          <div class="header-text-block">
            <h2 class="school-name-full mb-0">
              {{ campusName
              }}<span v-if="campusBranch" class="text-muted">
                - {{ campusBranch }}</span
              >
            </h2>
            <div class="report-info-line mt-1">
              <span class="report-name-text">{{ reportSubtitle }}</span>
              <span class="date-range-text">
                <strong>Generated on {{ currentDateTime }}</strong>
              </span>
            </div>
          </div>
        </div>
      </div>

      <section
        v-for="(campus, campusIndex) in campuses"
        :key="'campus-' + campus.campusID + '-' + campusIndex"
        class="campus-block"
      >
        <div v-if="campuses.length > 1" class="campus-block__banner">
          {{ campus.branch || campus.campusName }}
        </div>

        <div class="summary-section mb-3">
          <div class="summary-card summary-card--total">
            <span class="label">Total Students</span>
            <span class="value">{{ formatCount(campusTotalStudents(campus)) }}</span>
          </div>
          <div class="summary-card summary-card--free">
            <span class="label">Free Students</span>
            <span class="value">{{ formatCount(campusFreeStudents(campus)) }}</span>
          </div>
          <div class="summary-card summary-card--fee">
            <span class="label">Fee Status Students</span>
            <span class="value">{{
              formatCount(campusFeeStatusStudents(campus))
            }}</span>
          </div>
          <div class="summary-card summary-card--money">
            <span class="label">Monthly Fee Total</span>
            <span class="value value--money">{{
              formatAmount(campusMonthlyFeeTotal(campus))
            }}</span>
          </div>
          <div v-if="showAverageSection" class="summary-card summary-card--avg">
            <span class="label">Campus Avg Fee</span>
            <span class="value value--money">{{
              formatAmount(campus.campusAverageFee)
            }}</span>
            <span class="summary-card__hint">Per total enrolled student</span>
          </div>
        </div>

        <template v-if="showFeeStatusSection">
          <h5 class="table-block-title">Fee Status Report</h5>
          <div class="table-container">
            <table class="ledger-style-table">
              <thead>
                <tr>
                  <th class="text-center col-sno">#</th>
                  <th class="text-left">CLASS</th>
                  <th class="text-center">SECTION</th>
                  <th class="text-right">TOTAL STUDENTS</th>
                  <th class="text-right">FREE</th>
                  <th class="text-right">FEE STATUS</th>
                  <th class="text-right">MONTHLY FEE TOTAL</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, index) in campus.classSections"
                  :key="'fs-' + campusIndex + '-' + index"
                >
                  <td class="text-center font-weight-bold">{{ index + 1 }}</td>
                  <td class="text-left font-weight-bold class-cell">
                    {{ row.className }}
                  </td>
                  <td class="text-center">{{ row.sectionName }}</td>
                  <td class="text-right">{{ formatCount(row.totalStudents) }}</td>
                  <td class="text-right">{{ formatCount(row.freeStudents) }}</td>
                  <td class="text-right">
                    {{ formatCount(row.feeStatusStudents) }}
                  </td>
                  <td class="text-right font-weight-bold amount-cell">
                    {{ formatAmount(row.feeTotal) }}
                  </td>
                </tr>
                <tr class="totals-row">
                  <td colspan="3" class="text-right font-weight-bold">
                    CAMPUS TOTAL
                  </td>
                  <td class="text-right font-weight-bold">
                    {{ formatCount(campusTotalStudents(campus)) }}
                  </td>
                  <td class="text-right font-weight-bold">
                    {{ formatCount(campusFreeStudents(campus)) }}
                  </td>
                  <td class="text-right font-weight-bold">
                    {{ formatCount(campusFeeStatusStudents(campus)) }}
                  </td>
                  <td class="text-right font-weight-bold amount-cell">
                    {{ formatAmount(campusMonthlyFeeTotal(campus)) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>

        <template v-if="showAverageSection">
          <div class="avg-section-head">
            <h5
              class="table-block-title mb-0"
              :class="{ 'table-block-title--spaced': showFeeStatusSection }"
            >
              Average Fees
            </h5>
            <p class="avg-section-head__note mb-0">
              Average monthly fee = section monthly total ÷ total students
              (includes free and fee-status students).
            </p>
          </div>
          <div class="table-container table-container--avg">
            <table class="ledger-style-table ledger-style-table--avg">
              <thead>
                <tr>
                  <th class="text-center col-sno">#</th>
                  <th class="text-left">CLASS</th>
                  <th class="text-center">SECTION</th>
                  <th class="text-right">TOTAL</th>
                  <th class="text-right col-free">FREE</th>
                  <th class="text-right">FEE STATUS</th>
                  <th class="text-right">MONTHLY TOTAL</th>
                  <th class="text-right col-avg">AVG / ALL STUDENTS</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, index) in campus.averageRows"
                  :key="'avg-' + campusIndex + '-' + index"
                >
                  <td class="text-center font-weight-bold">{{ index + 1 }}</td>
                  <td class="text-left font-weight-bold class-cell">
                    {{ row.className }}
                  </td>
                  <td class="text-center">{{ row.sectionName }}</td>
                  <td class="text-right">
                    {{ formatCount(row.totalStudents) }}
                  </td>
                  <td class="text-right col-free">
                    {{ formatCount(row.freeStudents) }}
                  </td>
                  <td class="text-right">
                    {{ formatCount(row.feeStatusStudents) }}
                  </td>
                  <td class="text-right amount-cell">
                    {{ formatAmount(row.feeTotal) }}
                  </td>
                  <td class="text-right font-weight-bold amount-cell col-avg">
                    {{ formatAverage(row.averageFee) }}
                  </td>
                </tr>
                <tr class="totals-row totals-row--avg">
                  <td colspan="3" class="text-right font-weight-bold">
                    CAMPUS TOTAL
                  </td>
                  <td class="text-right font-weight-bold">
                    {{ formatCount(campusTotalStudents(campus)) }}
                  </td>
                  <td class="text-right font-weight-bold col-free">
                    {{ formatCount(campusFreeStudents(campus)) }}
                  </td>
                  <td class="text-right font-weight-bold">
                    {{ formatCount(campusFeeStatusStudents(campus)) }}
                  </td>
                  <td class="text-right font-weight-bold amount-cell">
                    {{ formatAmount(campusMonthlyFeeTotal(campus)) }}
                  </td>
                  <td class="text-right font-weight-bold amount-cell col-avg">
                    {{ formatAverage(campus.campusAverageFee) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </section>

      <div class="page-footer no-print-screen">
        End of report
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";
import {
  formatFeeReportAmount,
  normalizeFeeStatusResponse,
} from "@/utils/feeStatusReport";

export default {
  name: "FeeStatusReportPrint",
  components: { BButton, BSpinner, BBadge },
  data() {
    return {
      loading: false,
      error: null,
      campuses: [],
      db: "",
      campusID: 0,
      reportSection: "both",
      campusName: "MY SKOOL",
      campusBranch: "",
      schoolLogo: null,
    };
  },
  computed: {
    showFeeStatusSection() {
      return this.reportSection === "both" || this.reportSection === "status";
    },
    showAverageSection() {
      return this.reportSection === "both" || this.reportSection === "average";
    },
    reportToolbarTitle() {
      if (this.reportSection === "average") return "Average Fees Report";
      if (this.reportSection === "status") return "Fee Status Report";
      return "Fee Status Report";
    },
    reportSubtitle() {
      if (this.reportSection === "average") return "Average Fees";
      if (this.reportSection === "status") return "Fee Status Report";
      return "Fee Status and Average Fees";
    },
    loadingMessage() {
      if (this.reportSection === "average") {
        return "Loading average fees report...";
      }
      return "Loading fee status report...";
    },
    currentDateTime() {
      return new Date().toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
    totalSectionCount() {
      return (this.campuses || []).reduce((sum, c) => {
        const sections = c.classSections || [];
        return sum + sections.length;
      }, 0);
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    initReport() {
      const query = this.$route.query;
      this.db = query.db || this.$store.state.userData.db;
      this.campusID = Number(query.campusID || this.$store.state.userData.cId);
      const section = String(query.section || "both").toLowerCase();
      if (["both", "status", "average"].includes(section)) {
        this.reportSection = section;
      } else {
        this.reportSection = "both";
      }
      if (!this.db) {
        this.error =
          "Database identifier is missing. Please close and try again.";
        return;
      }
      if (!this.campusID) {
        this.error = "Please select a campus on the reports page and try again.";
        return;
      }
      this.loadCampusInfo();
      this.loadReportData();
    },
    async loadCampusInfo() {
      try {
        const response = await axios.get(
          "https://apiv26.myskool.app/api/campuses",
          {
            params: { db: this.db, cId: this.campusID },
            headers: {
              Authorization: `Bearer ${this.$store.state.userData.token}`,
              accept: "application/json",
            },
          }
        );
        if (
          response.data &&
          response.data.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          const match = response.data.data.find((c) => c.id == this.campusID);
          if (match) {
            this.campusName = match.name || this.campusName;
            this.campusBranch = match.branch || "";
            this.schoolLogo = match.logo || null;
          }
        }
      } catch (e) {
        const branches = this.$store.state.branches || [];
        const currentBranch =
          branches.find((b) => b.id == this.campusID) ||
          this.$store.state.currentBranch;
        if (currentBranch) {
          this.campusName =
            currentBranch.organizationName ||
            currentBranch.branch ||
            this.campusName;
          this.campusBranch = currentBranch.branch || "";
        }
      }
    },
    async loadReportData() {
      this.loading = true;
      this.error = null;
      try {
        const response = await axios.get(
          "https://apiv26.myskool.app/api/Students/FeeStatusReport",
          {
            params: {
              db: this.db,
              campusID: this.campusID,
            },
            headers: {
              Authorization: `Bearer ${this.$store.state.userData.token}`,
              accept: "application/json",
            },
          }
        );
        const parsed = normalizeFeeStatusResponse(response.data);
        if (parsed.message && !parsed.campuses.length) {
          this.error = parsed.message;
          this.campuses = [];
        } else {
          this.campuses = parsed.campuses;
        }
      } catch (err) {
        console.error(err);
        this.error = "Failed to load fee status report.";
        this.campuses = [];
      } finally {
        this.loading = false;
      }
    },
    formatAmount(value) {
      return formatFeeReportAmount(value);
    },
    formatAverage(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) return "0";
      return n.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    formatCount(value) {
      const n = Number(value);
      return Number.isFinite(n) ? n.toLocaleString() : "0";
    },
    campusTotalStudents(campus) {
      if (!campus) return 0;
      return campus.grandTotalStudents != null
        ? campus.grandTotalStudents
        : campus.totalStudents;
    },
    campusFreeStudents(campus) {
      if (!campus) return 0;
      return campus.grandTotalFreeStudents != null
        ? campus.grandTotalFreeStudents
        : campus.freeStudents;
    },
    campusFeeStatusStudents(campus) {
      if (!campus) return 0;
      return campus.grandTotalFeeStatusStudents != null
        ? campus.grandTotalFeeStatusStudents
        : campus.feeStatusStudents;
    },
    campusMonthlyFeeTotal(campus) {
      if (!campus) return 0;
      return campus.grandSumMonthlyFee != null
        ? campus.grandSumMonthlyFee
        : campus.feeTotal;
    },
    print() {
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.student-email-report {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2.5rem;
  font-family: "Outfit", "Inter", sans-serif;
  color: #2d3436;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1.25rem 2.5rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.3);

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.25rem;
    letter-spacing: -0.5px;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  span {
    margin-left: 4px;
  }

  &:active {
    transform: scale(0.96);
  }
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;

  &:hover {
    background: linear-gradient(135deg, #5b4cc4 0%, #8c82eb 100%);
    box-shadow: 0 6px 20px rgba(108, 92, 231, 0.35);
    transform: translateY(-1px);
  }
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;

  &:hover {
    background: #fdfdfd;
    color: #e74c3c;
    border-color: #fab1a0;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
    transform: translateY(-1px);
  }
}

.report-page {
  background: #ffffff;
  padding: 3rem 2.5rem;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  width: 98%;
  max-width: 1200px;
  margin: 30px auto;
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.report-header-modern {
  border-bottom: 2px solid #333;
  padding-bottom: 20px;

  .school-logo-img {
    height: 130px;
    width: auto;
    object-fit: contain;
  }

  .school-name-full {
    font-size: 2rem;
    font-weight: 800;
    color: #000;
    text-transform: uppercase;
    letter-spacing: -0.5px;
  }

  .report-info-line {
    font-size: 1.1rem;
    color: #444;
    display: flex;
    align-items: center;
    flex-wrap: wrap;

    .report-name-text {
      font-weight: 700;
      border-right: 2px solid #ddd;
      padding-right: 15px;
      margin-right: 15px;
    }

    .date-range-text {
      color: #555;
    }
  }
}

.summary-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 0.75rem;
  padding: 1.25rem 1rem;
  background: linear-gradient(180deg, #f8f9fa 0%, #fff 100%);
  border-radius: 12px;
  border: 1px solid #e9ecef;

  .summary-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0.65rem 0.5rem;
    border-radius: 10px;
    background: #fff;
    border: 1px solid #eee;
    min-width: 0;

    &--free .value {
      color: #0ea5e9;
    }

    &--fee .value {
      color: #7367f0;
    }

    &--avg {
      border-color: rgba(115, 103, 240, 0.25);
      background: rgba(115, 103, 240, 0.04);
    }

    &__hint {
      margin-top: 0.2rem;
      font-size: 0.58rem;
      font-weight: 600;
      color: #94a3b8;
      text-align: center;
      line-height: 1.2;
    }

    .label {
      font-size: 0.68rem;
      font-weight: 700;
      color: #636e72;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      margin-bottom: 4px;
      text-align: center;
    }

    .value {
      font-size: 1.15rem;
      font-weight: 800;
      color: #2d3436;
      font-variant-numeric: tabular-nums;

      &--money {
        color: #1a1a1a;
      }
    }
  }
}

.avg-section-head {
  margin-bottom: 0.5rem;

  &__note {
    font-size: 0.78rem;
    color: #636e72;
    margin-top: 0.35rem;
    max-width: 42rem;
  }
}

.ledger-style-table--avg {
  .col-free {
    background: rgba(14, 165, 233, 0.06);
  }

  .col-avg {
    background: rgba(115, 103, 240, 0.06);
  }

  tbody td.col-free {
    color: #0369a1;
    font-weight: 600;
  }

  tbody td.col-avg {
    color: #5e50ee;
  }

  .totals-row--avg td.col-free {
    color: #0c4a6e !important;
  }
}

.table-container--avg {
  overflow-x: auto;
}

.campus-block {
  margin-bottom: 2.5rem;

  &:not(:last-child) {
    padding-bottom: 2rem;
    border-bottom: 1px dashed #dfe6e9;
  }

  &__banner {
    text-align: center;
    font-size: 1rem;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #fff;
    background: linear-gradient(135deg, #2c3e50 0%, #4a5568 100%);
    padding: 0.55rem 1rem;
    border-radius: 8px;
    margin-bottom: 1rem;
  }
}

.table-block-title {
  font-size: 0.95rem;
  font-weight: 800;
  color: #444;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin: 0 0 0.35rem;

  &--spaced {
    margin-top: 1.75rem;
  }
}

.ledger-style-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 0.5rem;
  border: 1px solid #dee2e6;
  background: white;

  .col-sno {
    width: 5%;
  }

  th,
  td {
    border: 1px solid #dee2e6;
    padding: 12px 15px;
    box-sizing: border-box;
    font-size: 0.95rem;
  }

  thead {
    background: #2c3e50 !important;

    th {
      color: white !important;
      font-weight: 800;
      text-transform: uppercase;
      font-size: 0.82rem;
      letter-spacing: 0.5px;
    }
  }

  tbody tr:nth-child(even) {
    background-color: #f8f9fa;
  }

  tbody tr:hover {
    background: #f1f3f5;
  }

  .class-cell {
    color: #2d3436;
  }

  .amount-cell {
    font-variant-numeric: tabular-nums;
    color: #1a1a1a;
  }

  .totals-row td {
    background: #e9ecef !important;
    border-top: 2px solid #2c3e50 !important;
    color: #1a1a1a !important;
    font-size: 0.9rem;
  }
}

.page-footer {
  text-align: center;
  padding-top: 2rem;
  margin-top: 1rem;
  border-top: 1px solid #eee;
  color: #adb5bd;
  font-size: 0.85rem;
}

@page {
  size: A4 portrait;
  margin: 0;
}

@media print {
  .no-print,
  .no-print-screen {
    display: none !important;
  }

  body {
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
    color: #000 !important;
  }

  .student-email-report {
    background: white !important;
    padding: 0 !important;
  }

  .report-page {
    box-shadow: none !important;
    border: none !important;
    padding: 1.2cm 0.8cm !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
  }

  .summary-section {
    margin: 8px 0 12px !important;
    padding: 8px !important;
    border: 1px solid #000 !important;
    break-inside: avoid;

    .summary-card .label {
      font-size: 0.65rem !important;
    }

    .summary-card .value {
      font-size: 0.95rem !important;
    }
  }

  .ledger-style-table {
    font-size: 9pt !important;
    width: 100% !important;

    th,
    td {
      padding: 6px 10px !important;
      border: 1px solid #000 !important;
      color: #000 !important;
    }

    thead th {
      background: #f1f2f6 !important;
      color: #000 !important;
      font-weight: 800 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    tbody tr:hover {
      background: transparent !important;
    }

    .totals-row td {
      background: #e9ecef !important;
      border-top: 2px solid #000 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }

  .report-header-modern {
    margin-bottom: 25px !important;
    padding-bottom: 15px !important;
    border-bottom: 4px solid #000 !important;

    .school-logo-img {
      height: 140px !important;
      max-width: 350px !important;
      object-fit: contain !important;
    }

    .school-name-full {
      font-size: 1.8rem !important;
    }
  }

  .campus-block__banner {
    background: #333 !important;
    color: #fff !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}
</style>
